import { useMapStore } from '@/stores/map/mapStore';
import { usePageTheme } from '@/stores/page-theme';
import Link from 'next/link';
import { useEffect, useState } from 'react';
import { useStore } from 'zustand';
import IconComponent from '../FlashCards/Icon';
import ModalLocationChange from '../map/component/modal-location-change';

interface Props {
  ignoreTheme?: boolean;
  mobileView?: boolean;
  fetchData?: boolean;
}

const LocationWrapper = ({
  iconColor,
  children,
  fontColor,
  ignoreTheme,
  icon,
}: {
  icon: string;
  iconColor: string;
  fontColor: string;
  ignoreTheme: boolean | undefined;
  children: React.ReactNode;
}) => {
  return (
    <>
      <IconComponent name={icon} className={`h-10 w-10 ${fontColor}`} />
      <div
        className={`text-small flex w-[250px] flex-col justify-center sm:text-xs ${ignoreTheme ? '' : fontColor}`}
      >
        {children}
      </div>
    </>
  );
};

export default function LocationWidget({ fetchData = true, ...props }: Props) {
  const { ignoreTheme, mobileView } = props;
  const { fontColor } = useStore(usePageTheme, (state) => ({
    fontColor: state.fontColor,
    bgColor: state.bgColor,
    themeBase: state.themeBase,
  }));
  const {
      yourAddress,
      clientLocation,
      getCurrentPosition,
      setModalLocationChangeOpen,
      modalLocationChangeOpen,
      hasLocation,
      setHasLocation,
      address,
    } = useStore(useMapStore, (state) => ({
      getCurrentPosition: state.getCurrentPosition,
      yourAddress: state.yourAddress,
      clientLocation: state.clientLocation,
      setModalLocationChangeOpen: state.setModalLocationChangeOpen,
      modalLocationChangeOpen: state.modalLocationChangeOpen,
      hasLocation: state.hasLocation,
      setHasLocation: state.setHasLocation,
      address: state.address,
    })),
    [city, setCity] = useState<string>(''),
    [state, setState] = useState<string>(''),
    [country, setCountry] = useState<string>(''),
    [from, setFrom] = useState<string>('');

  const handleLocationChange = () => {
    setModalLocationChangeOpen(true);
  };

  useEffect(() => {
    setModalLocationChangeOpen(modalLocationChangeOpen);
  }, [modalLocationChangeOpen]);

  useEffect(() => {
    if (!address.selected) {
      return;
    }

    const [city, stateZip, country] = address.selected.address.split(',');
    const [state, zip] = (typeof stateZip === 'string' &&
      stateZip.trim().split(' ')) || ['', ''];

    setCity(city);
    setState(state);
    setCountry(country);
  }, [address]);

  useEffect(() => {
    if (yourAddress?.address_components.length) {
      const city =
        yourAddress.address_components.find((c) => c.types.includes('locality'))
          ?.long_name || '';
      const state =
        yourAddress.address_components.find((c) =>
          c.types.includes('administrative_area_level_1')
        )?.short_name || '';
      const country =
        yourAddress.address_components.find((c) => c.types.includes('country'))
          ?.long_name || '';
      const from =
        yourAddress.from === 'ip'
          ? 'IP address'
          : yourAddress.from === 'geoloc'
            ? 'Device'
            : '';

      setCity(city);
      setState(state);
      setCountry(country);
      setFrom(from);
      setHasLocation(true);
    } else {
      setHasLocation(false);
    }
  }, [yourAddress]);

  useEffect(() => {
    fetchData && !hasLocation && getCurrentPosition();
  }, []);

  return (
    <>
      <div className='mx-auto flex max-w-64 flex-grow-0 justify-center gap-3 rounded-full border px-2 py-1 font-bold md:mx-0'>
        {!hasLocation ? (
          <>
            <LocationWrapper
              icon='MdOutlineAddLocationAlt'
              ignoreTheme={props.ignoreTheme}
              fontColor={fontColor.primary}
              iconColor='text-blue-500'
            >
              <div>
                Location updating &bull;{' '}
                <Link onClick={handleLocationChange} href={'#'}>
                  update
                </Link>
              </div>
            </LocationWrapper>
          </>
        ) : (
          <>
            {clientLocation.browserLocationBlocked &&
            !clientLocation.manuallySet &&
            !clientLocation.ipAddress ? (
              <>
                <LocationWrapper
                  icon='MdOutlineLocationOff'
                  ignoreTheme={props.ignoreTheme}
                  fontColor={fontColor.primary}
                  iconColor='text-orange-500'
                >
                  <div>
                    Location unavailable &bull;{' '}
                    <Link onClick={handleLocationChange} href={'#'}>
                      update
                    </Link>
                  </div>
                </LocationWrapper>
              </>
            ) : clientLocation.outsideGeoFence &&
              !clientLocation.manuallySet &&
              !clientLocation.ipAddress ? (
              <>
                <LocationWrapper
                  icon='MdOutlineShareLocation'
                  ignoreTheme={props.ignoreTheme}
                  fontColor={fontColor.primary}
                  iconColor='text-orange-500'
                >
                  {' '}
                  <div>
                    {city}, {state}
                  </div>
                  <div>
                    Outside GeoFence &bull;{' '}
                    <Link onClick={handleLocationChange} href={'#'}>
                      update
                    </Link>
                  </div>
                </LocationWrapper>
              </>
            ) : (
              <>
                <LocationWrapper
                  icon='MdOutlinePinDrop'
                  ignoreTheme={props.ignoreTheme}
                  fontColor={fontColor.primary}
                  iconColor='text-gray-900'
                >
                  <div>
                    {city}, {state}
                  </div>
                  <div>
                    {clientLocation.manuallySet
                      ? 'Manually Set'
                      : `From your ${from}`}{' '}
                    &bull;{' '}
                    <Link onClick={handleLocationChange} href={'#'}>
                      update
                    </Link>
                  </div>
                </LocationWrapper>
              </>
            )}
          </>
        )}
      </div>
      <ModalLocationChange modalOpen={modalLocationChangeOpen} />
    </>
  );
}
